// Copyright 2026, University of Maryland, All Rights Reserved
import { createContext, useContext, type ReactNode } from 'react'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SidebarContextValue {
  isOpen: boolean
  setOpen: (open: boolean) => void
  newAgentId: string | null
  setNewAgentId: (agentId: string) => void
}

const SidebarContext = createContext<SidebarContextValue | null>(null)

export function SidebarProvider({
  value,
  children,
}: {
  value: SidebarContextValue
  children: ReactNode
}) {
  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}

export function useSidebar(): SidebarContextValue | null {
  return useContext(SidebarContext)
}

export function SidebarToggle({ className }: { className?: string }) {
  const ctx = useSidebar()
  if (!ctx) return null

  return (
    <button
      type="button"
      data-testid="app-sidebar-toggle"
      onClick={() => ctx.setOpen(true)}
      aria-label="Open sidebar"
      aria-pressed={ctx.isOpen}
      aria-hidden={ctx.isOpen}
      tabIndex={ctx.isOpen ? -1 : 0}
      className={cn(
        'inline-flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-md text-slate-700 transition-[width,margin,opacity,background-color,transform] duration-[260ms] ease-[cubic-bezier(.32,.72,.32,1)] hover:bg-slate-100 active:scale-95',
        className,
        ctx.isOpen && 'pointer-events-none ml-0 mr-0 w-0 border-transparent p-0 opacity-0 shadow-none',
      )}
    >
      <Menu className="h-5 w-5" aria-hidden="true" />
    </button>
  )
}
